import { safe } from "@orpc/client";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import z from "zod";
import { useAppForm } from "@/components/form";
import { ComboboxCountry } from "@/components/forms/combobox-country";
import { ComboboxLanguage } from "@/components/forms/combobox-language";
import { ComboboxPublisher } from "@/components/forms/combobox-publisher";
import { ComboboxUserStatus } from "@/components/forms/combobox-user-status";
import { FieldGroup } from "@/components/ui/field";
import {
	FormCard,
	FormCardContent,
	FormCardHeader,
	FormCardTitle,
} from "@/components/ui/form-card";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupText,
} from "@/components/ui/input-group";
import { orpc, validationErrors } from "@/orpc/client";

export const Route = createFileRoute("/_app/users/$userId/")({
	component: Page,
	params: z.object({
		userId: z.coerce.number(),
	}),
	async loader({ context, params }) {
		context.queryClient.prefetchQuery(
			orpc.users.show.queryOptions({
				input: {
					userId: params.userId,
				},
			}),
		);
	},
});

function Page() {
	const params = Route.useParams();

	const { data: user } = useSuspenseQuery(
		orpc.users.show.queryOptions({
			input: {
				userId: params.userId,
			},
		}),
	);

	const updateMutation = useMutation(
		orpc.users.update.mutationOptions({
			onSuccess: (_data, _payload, _result, context) => {
				context.client.invalidateQueries({ queryKey: orpc.users.key() });
				toast.success("Channel updated successfully.");
			},
			onError: () => {
				toast.error("Failed to update this channel.");
			},
		}),
	);

	const form = useAppForm({
		defaultValues: {
			Country: user.Country,
			EmailAddress: user.EmailAddress,
			LanguageCode: user.LanguageCode.trim(),
			Publisher: user.Publisher.ID,
			StatusID: user.StatusID,
			UserName: user.UserName,
		},
		canSubmitWhenInvalid: true,
		validators: {
			onSubmitAsync: async ({ value }) => {
				const { error } = await safe(
					updateMutation.mutateAsync({
						...value,
						ID: user.ID,
					}),
				);

				if (error) {
					return (
						validationErrors(error) ??
						"An error occurred while updating the channel."
					);
				}
			},
		},
	});

	return (
		<form.AppForm>
			<form
				className="relative"
				id={form.formId}
				onSubmit={(e) => {
					e.preventDefault();
					e.stopPropagation();
					form.handleSubmit();
				}}
			>
				<div className="px-4 lg:px-6 grid grid-cols-12 gap-6">
					<FormCard className="col-span-12" disabled={updateMutation.isPending}>
						<FormCardHeader>
							<FormCardTitle>Channel Info</FormCardTitle>
						</FormCardHeader>
						<FormCardContent>
							<FieldGroup>
								<form.AppField
									name="Publisher"
									children={(field) => (
										<field.FormItem className="lg:col-span-6">
											<field.FormLabel>Publisher</field.FormLabel>
											<ComboboxPublisher
												id={`${field.form.formId}-publisher`}
												value={field.state.value}
												onChange={field.handleChange}
												onBlur={field.handleBlur}
												isInvalid={
													field.state.meta.isTouched &&
													!field.state.meta.isValid
												}
											/>
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="StatusID"
									children={(field) => (
										<field.FormItem className="lg:col-span-6">
											<field.FormLabel>Status</field.FormLabel>
											<ComboboxUserStatus
												id={`${field.form.formId}-status`}
												value={field.state.value}
												onChange={field.handleChange}
												onBlur={field.handleBlur}
												isInvalid={
													field.state.meta.isTouched &&
													!field.state.meta.isValid
												}
											/>
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="UserName"
									children={(field) => (
										<field.FormItem className="lg:col-span-6">
											<field.FormLabel>User Name</field.FormLabel>
											<InputGroup>
												<field.FormGroupInput />
												<InputGroupAddon align="block-end">
													<InputGroupText className="tabular-nums">
														{field.state.value.length}/100 characters
													</InputGroupText>
												</InputGroupAddon>
											</InputGroup>
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="EmailAddress"
									children={(field) => (
										<field.FormItem className="lg:col-span-6">
											<field.FormLabel>Email Address</field.FormLabel>
											<InputGroup>
												<field.FormGroupInput />
												<InputGroupAddon align="block-end">
													<InputGroupText className="tabular-nums">
														{field.state.value.length}/100 characters
													</InputGroupText>
												</InputGroupAddon>
											</InputGroup>
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="Country"
									children={(field) => (
										<field.FormItem className="lg:col-span-6">
											<field.FormLabel>Country</field.FormLabel>
											<ComboboxCountry
												id={`${field.form.formId}-country`}
												value={field.state.value}
												onChange={field.handleChange}
												onBlur={field.handleBlur}
												isInvalid={
													field.state.meta.isTouched &&
													!field.state.meta.isValid
												}
											/>
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="LanguageCode"
									children={(field) => (
										<field.FormItem className="lg:col-span-6">
											<field.FormLabel>Language</field.FormLabel>
											<ComboboxLanguage
												id={`${field.form.formId}-language`}
												value={field.state.value}
												onChange={field.handleChange}
												onBlur={field.handleBlur}
												isInvalid={
													field.state.meta.isTouched &&
													!field.state.meta.isValid
												}
											/>
											<field.FormError />
										</field.FormItem>
									)}
								/>
							</FieldGroup>
						</FormCardContent>
					</FormCard>
					<form.FormSaveBar />
				</div>
				<form.FormBottomBar />
			</form>
		</form.AppForm>
	);
}
